import {
  BaseLayoutManager,
  detectMode,
  detectOrientation,
} from '@bn-games/layout-core';
import {
  BOARD_COLS,
  BOARD_CONTROLS_SEAM_PX,
  BOARD_FRAME_THICKNESS_MOBILE_PX,
  BOARD_FRAME_THICKNESS_PX,
  BOARD_PADDING_DESKTOP_PX,
  BOARD_PADDING_MOBILE_PX,
  BOARD_ROWS,
  CONTROLS_INNER_PADDING_PX,
  GRID_LINE_THICK_PX,
  GRID_LINE_THIN_PX,
  MAX_CELL_SIZE_PX,
  MIN_CELL_SIZE_PX,
  NUMPAD_ACTION_COLUMNS,
  NUMPAD_ACTION_LABEL_FONT_PX,
  NUMPAD_ACTION_LABEL_GAP_PX,
  NUMPAD_BUTTON_GAP_PX,
  NUMPAD_DIGIT_COLUMNS,
  NUMPAD_MAX_BUTTON_PX,
  PAUSE_BUTTON_SIZE_PX,
  STATUS_BAR_HEIGHT_PX,
  STATUS_BAR_HORIZONTAL_PADDING_PX,
} from '../utils/constants.js';
import type { NonEmptyDigit } from '../game/types.js';
import type {
  CellHit,
  Layout,
  LayoutMode,
  NumpadButtonId,
  NumpadButtonRegion,
  PauseButtonRegion,
} from './types.js';

/** Action row first (undo / erase / pencil / hint), then digit row (1-9).
 *  Hit-testing iterates in this order; the renderer uses it to know which
 *  buttons need the icon+caption treatment vs. the bold-digit treatment. */
const ACTION_ROW_IDS: readonly NumpadButtonId[] = ['undo', 'erase', 'pencil', 'hint'];

// Edge-to-edge layout (no outer padding, trays span the full wood-frame
// interior) is the touch-screen treatment. Desktop keeps the inset frame
// because there's room — and a screen with a mouse doesn't benefit from
// edge bleed the same way.
function isEdgeToEdgeMode(mode: LayoutMode): boolean {
  return mode !== 'desktop';
}

function pickPadding(mode: LayoutMode): number {
  return isEdgeToEdgeMode(mode) ? BOARD_PADDING_MOBILE_PX : BOARD_PADDING_DESKTOP_PX;
}

function pickFrameThickness(mode: LayoutMode): number {
  return isEdgeToEdgeMode(mode) ? BOARD_FRAME_THICKNESS_MOBILE_PX : BOARD_FRAME_THICKNESS_PX;
}

/**
 * Sizes for one row of equal-width buttons so the row fills `interiorWidth`
 * exactly. The base is `floor(usable / columns)`; the leftover pixels go to
 * the leftmost `remainder` columns (each gets +1). No max cap on width —
 * action buttons (4 cols) end up roughly twice as wide as digit buttons (9
 * cols), which matches the screenshot's "spacious actions, dense numpad".
 */
function rowMetrics(interiorWidth: number, columns: number): { base: number; remainder: number } {
  const gapTotal = (columns - 1) * NUMPAD_BUTTON_GAP_PX;
  const usable = Math.max(0, interiorWidth - gapTotal);
  const base = Math.floor(usable / columns);
  const remainder = usable - base * columns;
  return { base, remainder };
}

/** Hand out the remainder pixels one per button to the leftmost columns. */
function distribute(base: number, remainder: number, count: number): number[] {
  const widths: number[] = [];
  for (let i = 0; i < count; i += 1) widths.push(base + (i < remainder ? 1 : 0));
  return widths;
}

function colXFor(startX: number, widths: readonly number[], col: number): number {
  let x = startX;
  for (let i = 0; i < col; i += 1) x += (widths[i] ?? 0) + NUMPAD_BUTTON_GAP_PX;
  return x;
}

/**
 * Two-row button grid inside the controls tray:
 *   row 0 (actions): undo / erase / pencil / hint — 4 buttons, taller (icon + caption).
 *   row 1 (digits):  digit-1 … digit-9 — single 9-button strip with the bold-blue glyph.
 */
function buildNumpadButtons(
  interiorX: number,
  interiorY: number,
  interiorWidth: number,
  digitButtonHeight: number,
  actionRowHeight: number,
): NumpadButtonRegion[] {
  const buttons: NumpadButtonRegion[] = [];

  const action = rowMetrics(interiorWidth, NUMPAD_ACTION_COLUMNS);
  const actionWidths = distribute(action.base, action.remainder, NUMPAD_ACTION_COLUMNS);
  for (let i = 0; i < NUMPAD_ACTION_COLUMNS; i += 1) {
    const id = ACTION_ROW_IDS[i];
    if (id === undefined) continue;
    buttons.push({
      id,
      x: colXFor(interiorX, actionWidths, i),
      y: interiorY,
      width: actionWidths[i] ?? action.base,
      height: actionRowHeight,
    });
  }

  const digit = rowMetrics(interiorWidth, NUMPAD_DIGIT_COLUMNS);
  const digitWidths = distribute(digit.base, digit.remainder, NUMPAD_DIGIT_COLUMNS);
  const digitRowY = interiorY + actionRowHeight + NUMPAD_BUTTON_GAP_PX;
  for (let i = 0; i < NUMPAD_DIGIT_COLUMNS; i += 1) {
    const d = (i + 1) as NonEmptyDigit;
    buttons.push({
      id: `digit-${d}`,
      x: colXFor(interiorX, digitWidths, i),
      y: digitRowY,
      width: digitWidths[i] ?? digit.base,
      height: digitButtonHeight,
    });
  }

  return buttons;
}

/** Height of the controls tray (action row + gap + digit row + inner padding). */
function controlsTrayHeight(digitButtonHeight: number, actionRowHeight: number): number {
  return (
    actionRowHeight + NUMPAD_BUTTON_GAP_PX + digitButtonHeight +
    CONTROLS_INNER_PADDING_PX * 2
  );
}

/** Digit-button height derived from the digit-row column width — capped at
 *  `NUMPAD_MAX_BUTTON_PX` so the tray doesn't balloon on wide viewports. */
function digitButtonSizeFor(trayWidth: number): number {
  const interior = trayWidth - CONTROLS_INNER_PADDING_PX * 2;
  const { base } = rowMetrics(interior, NUMPAD_DIGIT_COLUMNS);
  return Math.min(NUMPAD_MAX_BUTTON_PX, base);
}

/** Action-row height = button face + caption gap + caption font. The action
 *  row reserves room for the small "Undo" / "Erase" / "Notes" / "Hint" label
 *  painted under each icon. */
function actionRowHeightFor(digitButtonHeight: number): number {
  return digitButtonHeight + NUMPAD_ACTION_LABEL_GAP_PX + NUMPAD_ACTION_LABEL_FONT_PX;
}

/**
 * Pick the largest cell size that still lets the 9×9 grid + the controls
 * tray it implies fit inside `verticalBudget`, capped by `horizontalBudget`
 * and the global MIN/MAX. On desktop the tray width tracks the board width;
 * on mobile the tray spans the viewport, so we pass `fixedTrayWidth` to
 * pin the digit-button sizing independent of the chosen cell size.
 */
function chooseCellSize(
  horizontalBudget: number,
  verticalBudget: number,
  fixedTrayWidth: number | null,
): number {
  const horizontalCap = Math.floor(horizontalBudget / BOARD_COLS);
  const start = Math.min(MAX_CELL_SIZE_PX, Math.max(MIN_CELL_SIZE_PX, horizontalCap));
  for (let cellSize = start; cellSize > MIN_CELL_SIZE_PX; cellSize -= 1) {
    const boardSize = cellSize * BOARD_ROWS;
    const trayWidth = fixedTrayWidth ?? boardSize;
    const digitButtonHeight = digitButtonSizeFor(trayWidth);
    const actionRowHeight = actionRowHeightFor(digitButtonHeight);
    const trayHeight = controlsTrayHeight(digitButtonHeight, actionRowHeight);
    if (boardSize + trayHeight <= verticalBudget) return cellSize;
  }
  return MIN_CELL_SIZE_PX;
}

export class LayoutManager extends BaseLayoutManager<Layout> {
  protected compute(): Layout {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const mode = detectMode(viewportWidth);
    const orientation = detectOrientation(viewportWidth, viewportHeight);
    const isMobile = mode === 'mobile';
    const edgeToEdge = isEdgeToEdgeMode(mode);
    const padding = pickPadding(mode);
    const frame = pickFrameThickness(mode);
    const seam = BOARD_CONTROLS_SEAM_PX;

    const horizontalBudget = Math.max(0, viewportWidth - padding * 2 - frame * 2);
    // Wood frame now wraps the full stack:
    //   status tray + seam + 9×9 board + seam + numpad tray.
    // Two seams — one above the board, one below — match each other and the
    // verticalBudget reserves room for both.
    const verticalBudget = Math.max(
      0,
      viewportHeight - padding * 2 - STATUS_BAR_HEIGHT_PX - frame * 2 - seam * 2,
    );

    // Mobile + tablet pin the tray width to the full wood-frame interior so
    // the bars read as full-screen and the digit row gets bigger touch
    // targets. Desktop lets the tray track the board width.
    const edgeToEdgeTrayWidth = edgeToEdge ? viewportWidth - frame * 2 : null;
    const cellSize = chooseCellSize(horizontalBudget, verticalBudget, edgeToEdgeTrayWidth);
    const boardSize = cellSize * BOARD_ROWS;
    const trayWidth = edgeToEdgeTrayWidth ?? boardSize;
    const digitButtonHeight = digitButtonSizeFor(trayWidth);
    const actionRowHeight = actionRowHeightFor(digitButtonHeight);
    const numpadMinHeight = controlsTrayHeight(digitButtonHeight, actionRowHeight);

    // Leftover vertical space is poured INTO the trays (split evenly between
    // top and bottom) rather than parked outside the wood frame. The board
    // keeps its natural size; the trays absorb the rest so the frame reads
    // edge-to-edge and the stats / numpad buttons float vertically centered
    // inside the larger wells.
    const stackMinHeight =
      STATUS_BAR_HEIGHT_PX + seam + boardSize + seam + numpadMinHeight + frame * 2;
    const verticalLeftover = Math.max(
      0,
      viewportHeight - padding * 2 - stackMinHeight,
    );
    const extraTop = Math.floor(verticalLeftover / 2);
    const extraBottom = verticalLeftover - extraTop;
    const statusBarHeight = STATUS_BAR_HEIGHT_PX + extraTop;
    const numpadHeight = numpadMinHeight + extraBottom;

    const boardX = Math.floor((viewportWidth - boardSize) / 2);
    // Desktop: trays sit INSIDE the wood frame, matching the board's
    // horizontal extent. Mobile + tablet: the wood frame extends to the
    // viewport edges (padding is 0), so the trays span the full frame
    // interior — wider than the board — with wood-frame side gutters between
    // the tray-wide bars and the board's narrower middle section.
    const statusBarX = edgeToEdge ? frame : boardX;
    const statusBarWidth = edgeToEdge ? viewportWidth - frame * 2 : boardSize;
    const numpadTrayX = edgeToEdge ? frame : boardX;
    const numpadTrayWidth = edgeToEdge ? viewportWidth - frame * 2 : boardSize;
    const statusBarY = padding + frame;
    const boardY = statusBarY + statusBarHeight + seam;
    const numpadY = boardY + boardSize + seam;

    const pauseButton: PauseButtonRegion = {
      x: statusBarX + statusBarWidth - STATUS_BAR_HORIZONTAL_PADDING_PX - PAUSE_BUTTON_SIZE_PX,
      y: statusBarY + Math.floor((statusBarHeight - PAUSE_BUTTON_SIZE_PX) / 2),
      width: PAUSE_BUTTON_SIZE_PX,
      height: PAUSE_BUTTON_SIZE_PX,
    };
    // Buttons spread inside the tray's interior: on desktop that's
    // board-width; on mobile + tablet it's the full viewport width so buttons
    // get bigger touch targets matching the wider strip. Vertically the
    // action+gap+digit block centers inside the (possibly expanded) tray
    // instead of hugging the top edge.
    const buttonBlockHeight =
      actionRowHeight + NUMPAD_BUTTON_GAP_PX + digitButtonHeight;
    const interiorX = numpadTrayX + CONTROLS_INNER_PADDING_PX;
    const interiorY = numpadY + Math.max(
      CONTROLS_INNER_PADDING_PX,
      Math.floor((numpadHeight - buttonBlockHeight) / 2),
    );
    const interiorWidth = numpadTrayWidth - CONTROLS_INNER_PADDING_PX * 2;
    const buttons = buildNumpadButtons(
      interiorX,
      interiorY,
      interiorWidth,
      digitButtonHeight,
      actionRowHeight,
    );

    return {
      mode,
      orientation,
      viewport: { width: viewportWidth, height: viewportHeight, isMobile },
      statusBar: {
        x: statusBarX,
        y: statusBarY,
        width: statusBarWidth,
        height: statusBarHeight,
        pauseButton,
      },
      board: {
        x: boardX,
        y: boardY,
        width: boardSize,
        height: boardSize,
        cellSize,
        gridLineThin: GRID_LINE_THIN_PX,
        gridLineThick: GRID_LINE_THICK_PX,
      },
      numpad: {
        x: numpadTrayX,
        y: numpadY,
        width: numpadTrayWidth,
        height: numpadHeight,
        buttonSize: digitButtonHeight,
        actionRowHeight,
        buttons,
      },
      frameThickness: frame,
      devicePixelRatio: window.devicePixelRatio || 1,
    };
  }

  protected equals(a: Layout, b: Layout): boolean {
    return (
      a.mode === b.mode &&
      a.orientation === b.orientation &&
      a.viewport.width === b.viewport.width &&
      a.viewport.height === b.viewport.height &&
      a.statusBar.x === b.statusBar.x &&
      a.statusBar.y === b.statusBar.y &&
      a.statusBar.width === b.statusBar.width &&
      a.statusBar.height === b.statusBar.height &&
      a.statusBar.pauseButton.x === b.statusBar.pauseButton.x &&
      a.statusBar.pauseButton.y === b.statusBar.pauseButton.y &&
      a.board.x === b.board.x &&
      a.board.y === b.board.y &&
      a.board.cellSize === b.board.cellSize &&
      a.numpad.x === b.numpad.x &&
      a.numpad.y === b.numpad.y &&
      a.numpad.buttonSize === b.numpad.buttonSize &&
      a.numpad.actionRowHeight === b.numpad.actionRowHeight &&
      a.numpad.buttons.length === b.numpad.buttons.length &&
      a.frameThickness === b.frameThickness &&
      a.devicePixelRatio === b.devicePixelRatio
    );
  }
}

export function cellAtPoint(layout: Layout, x: number, y: number): CellHit | null {
  const { board } = layout;
  if (x < board.x || y < board.y) return null;
  if (x >= board.x + board.width || y >= board.y + board.height) return null;
  const col = Math.floor((x - board.x) / board.cellSize);
  const row = Math.floor((y - board.y) / board.cellSize);
  if (row < 0 || row >= BOARD_ROWS || col < 0 || col >= BOARD_COLS) return null;
  return { row, col, index: row * BOARD_COLS + col };
}

export function numpadButtonAtPoint(
  layout: Layout,
  x: number,
  y: number,
): NumpadButtonRegion | null {
  for (const button of layout.numpad.buttons) {
    if (
      x >= button.x &&
      y >= button.y &&
      x < button.x + button.width &&
      y < button.y + button.height
    ) {
      return button;
    }
  }
  return null;
}

/** True when (x, y) lands inside the status-bar pause button.
 *  The tappable area is intentionally larger than the visible 40px circle:
 *  on touch screens the small target is hard to hit reliably (a finger pad
 *  rarely lands dead-center), so the hit region extends to the full status
 *  tray height and a bit past the visible button to the left. The painted
 *  pill keeps the same crisp size — only the input boundary grows. */
export function pauseButtonAtPoint(layout: Layout, x: number, y: number): boolean {
  const { pauseButton } = layout.statusBar;
  const { y: barY, height: barH, x: barX, width: barW } = layout.statusBar;
  // Vertically: anywhere inside the status tray counts.
  if (y < barY || y >= barY + barH) return false;
  // Horizontally: from a few pixels past the visible button's left edge
  // (so a finger just left of the icon still registers) to the tray's
  // right edge.
  const slop = Math.floor(pauseButton.width / 2);
  const left = pauseButton.x - slop;
  const right = barX + barW;
  return x >= left && x < right;
}
